import { FC } from 'react';
import { interpolateRgbBasis } from 'd3';
import { CombinedError } from 'urql';

import { useEnvironmentMetrics } from '@/hooks/discovery/environmentMetrics';
import { ProgressGauge } from '../Charts/ProgressGauge';

export interface MetricComponentProps {
  name: string;
  description: string;
  value: number | undefined;
  error: CombinedError | undefined;
  isLoading: boolean;
  // float situation?
  testId?: string;
}

export const INTERPOLATION_COLOR_MAP = {
  redToGreen: interpolateRgbBasis(['#EF4444', '#F59E0B', '#66C2C2']),
};

export const MetricComponent: FC<MetricComponentProps> = ({
  name,
  description,
  value,
  error,
  isLoading,
  testId = 'environment-metric',
}) => {
  return (
    <div
      className="flex flex-1 flex-row gap-6 rounded-lg bg-bgBackground p-6 text-fgMain"
      data-testid={testId}
    >
      <div className="flex w-[104px] flex-initial">
        <ProgressGauge
          value={value}
          error={error}
          isLoading={isLoading}
          testId={`${testId}-progress-gauge`}
        />
      </div>

      <div className="flex w-72 flex-1 flex-col space-y-2">
        <div className="text-md font-semibold">{name}</div>
        <div className="text-sm font-normal">{description}</div>
      </div>
    </div>
  );
};

export interface MetricSectionProps {
  environmentId: number;
}

export const MetricSection: FC<MetricSectionProps> = ({ environmentId }) => {
  const [metrics] = useEnvironmentMetrics({ environmentId });

  const results = metrics?.data?.recommendations?.metrics;
  const hasData = !!results;

  const docsCoverage = results?.find((x) => x.name === 'model_documentation_coverage');
  const testCoverage = results?.find((x) => x.name === 'model_test_coverage');

  return (
    <div className="flex flex-row flex-wrap gap-4">
      <MetricComponent
        error={metrics.error}
        isLoading={metrics.fetching}
        name="Models with Tests"
        description="Define tests on your models to help ensure data quality for consumers."
        value={hasData ? parseFloat(testCoverage?.value || '0') : undefined}
        testId="models-with-tests-environment-metric"
      />
      <MetricComponent
        error={metrics.error}
        isLoading={metrics.fetching}
        name="Models with Documentation"
        description="Document your models to ensure collaborators understand their purpose and contents."
        value={hasData ? parseFloat(docsCoverage?.value || '0') : undefined}
        testId="models-with-documentation-environment-metric"
      />
    </div>
  );
};
